export interface PrintOptions {
  pageSize?: 'A4' | 'letter' | 'label' | string;
  orientation?: 'portrait' | 'landscape';
  margins?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  labelSizeMm?: {
    width: number;
    height: number;
  };
  labelSelector?: string;
}

export class PrintService {
  static print(htmlContent: string, options: PrintOptions = {}) {
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      console.error('No se pudo abrir la ventana de impresión');
      return;
    }

    const {
      pageSize = 'label',
      orientation = 'portrait',
      margins = { top: 0, right: 0, bottom: 0, left: 0 },
      labelSizeMm = { width: 60, height: 30 },
      labelSelector = '.print-label-60x30',
    } = options;

    const styles = `
      <style>
        @page {
          size: ${typeof pageSize === 'string' && pageSize.includes('mm') ? pageSize : `${labelSizeMm.width}mm ${labelSizeMm.height}mm`} ${orientation};
          margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
        }
        
        body {
          font-family: 'Courier New', monospace;
          font-size: 10px;
          line-height: 1.2;
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .label {
          border: 2px solid black;
          display: inline-block;
          page-break-inside: avoid;
        }
        
        .label-large {
          width: 80mm;
          height: 120mm;
          padding: 2mm;
        }
        
        .label-small {
          width: 60mm !important;
          height: 30mm !important;
          padding: 1mm !important;
          font-size: 8px;
          background-color: #90EE90 !important;
          border: none !important;
          box-sizing: border-box;
        }
        
        .header {
          background-color: white !important;
          border: 2px solid black;
          text-align: center;
          font-weight: bold;
          padding: 1mm;
          margin-bottom: 1mm;
        }
        
        .code-section {
          background-color: white !important;
          border: 2px solid black;
          text-align: center;
          font-weight: bold;
          padding: 1mm;
          margin-bottom: 1mm;
          font-size: 8px;
        }
        
        .info-row {
          display: flex;
          border: 1px solid black;
          margin-bottom: 1px;
        }
        
        .info-label {
          background-color: white !important;
          border-right: 1px solid black;
          padding: 0.5mm 1mm;
          font-weight: bold;
          min-width: 25mm;
          font-size: 7px;
        }
        
        .info-value {
          padding: 0.5mm 1mm;
          flex: 1;
          font-size: 7px;
        }
        
        .observation-section {
          background-color: white !important;
          border: 1px solid black;
          height: 15mm;
          margin: 1mm 0;
          padding: 1mm;
        }
        
        .observation-title {
          font-weight: bold;
          text-align: center;
          font-size: 7px;
          margin-bottom: 1mm;
          background-color: transparent !important;
        }
        
        .observation-area {
          background-color: white !important;
          height: calc(100% - 4mm);
          min-height: 10mm;
          border: none !important;
          display: block !important;
          grid: none !important;
          grid-template-columns: none !important;
          grid-template-rows: none !important;
        }
        
        .footer-sections {
          display: flex;
          gap: 1mm;
          margin: 1mm 0;
        }
        
        .footer-section {
          flex: 1;
          background-color: white !important;
          border: 1px solid black;
          padding: 1mm;
          min-height: 8mm;
        }
        
        .footer-title {
          font-weight: bold;
          text-align: center;
          font-size: 6px;
          margin-bottom: 1mm;
          background-color: transparent !important;
        }
        
        .footer-area {
          background-color: white !important;
          height: calc(100% - 3mm);
          min-height: 5mm;
          border: none !important;
          display: block !important;
          grid: none !important;
          grid-template-columns: none !important;
          grid-template-rows: none !important;
        }
        
        /* Reglas adicionales para limpiar cualquier grid CSS */
        .observation-area *,
        .footer-area * {
          border: none !important;
          background: transparent !important;
        }
        
        /* Eliminar cualquier grid o tabla que pueda aparecer */
        div[class*="grid"],
        div[style*="grid"],
        div[style*="display: grid"] {
          display: block !important;
          grid: none !important;
        }
        
        .barcode-section {
          text-align: center;
          margin: 1mm 0;
          font-family: 'Libre Barcode 128', monospace;
          font-size: 24px;
          border: 1px solid black;
          padding: 1mm;
        }
        
        @media print {
          body { 
            margin: 0; 
            padding: 0;
            width: ${labelSizeMm.width}mm;
            height: ${labelSizeMm.height}mm;
          }
          .no-print { 
            display: none; 
          }
          @page { 
            size: ${labelSizeMm.width}mm ${labelSizeMm.height}mm; 
            margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm; 
          }
          ${labelSelector} { 
            page-break-inside: avoid; 
          }
        }

        ${labelSelector} {
          width: ${labelSizeMm.width}mm !important;
          height: ${labelSizeMm.height}mm !important;
          padding: 1.2mm !important;
          box-sizing: border-box;
          page-break-inside: avoid;
        }

        .print-label-60x100 {
          padding: 1mm !important;
        }

        ${labelSelector} svg {
          max-width: 100% !important;
          height: auto !important;
        }
        
        ${labelSelector} svg path {
          fill: #000000 !important;
          stroke: #000000 !important;
        }
        
        ${labelSelector} canvas {
          max-width: 100% !important;
          height: auto !important;
          image-rendering: pixelated !important;
          image-rendering: -moz-crisp-edges !important;
          image-rendering: crisp-edges !important;
        }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Impresión de Etiqueta</title>
          <meta charset="UTF-8">
          ${styles}
        </head>
        <body>
          ${htmlContent}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              setTimeout(() => window.close(), 200);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  }

  static printLabel(content: string, size: 'large' | 'small' = 'large') {
    const isSmall = size === 'small';

    const options: PrintOptions = {
      pageSize: `${isSmall ? '60mm 30mm' : '60mm 100mm'}`,
      orientation: 'portrait',
      margins: isSmall ? { top: 0, right: 0, bottom: 0, left: 0 } : { top: 0, right: 0, bottom: 0, left: 0 },
      labelSizeMm: isSmall ? { width: 60, height: 30 } : { width: 60, height: 100 },
      labelSelector: isSmall ? '.print-label-60x30' : '.print-label-60x100',
    };

    this.print(content, options);
  }
} 